import { useMemo } from 'react';
import { AritoFormV2, AritoIcon, BottomActionBarV2, DialogV2 } from '@/components/custom/erp';
import { formSchema, FormValues, initialValues } from '../../schemas';
import type { GiaMua, GiaMuaInput } from '@/types/schemas';
import { useSearchFieldStates } from '../../hooks';
import { FormMode } from '@/types/form';
import BasicInfo from './BasicInfo';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: GiaMuaInput) => Promise<void>;
  formMode: FormMode;
  initialData?: GiaMua | null;
  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
}

const normalizeDate = (value: unknown): string => {
  if (!value) return '';
  const str = String(value);
  // Extract YYYY-MM-DD from full ISO datetime (e.g. "2026-06-24T00:00:00+07:00")
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : str;
};

const FormDialog = ({
  open,
  onClose,
  onSubmit,
  formMode,
  initialData,
  onAddButtonClick,
  onEditButtonClick,
  onDeleteButtonClick,
  onCopyButtonClick
}: FormDialogProps) => {
  const searchFields = useSearchFieldStates(initialData);
  const { vatTu, donViTinh, nhaCungCap, ngoaiTe } = searchFields;

  const normalizedInitialData = useMemo(() => {
    if (!initialData) return initialValues;
    return {
      ...initialData,
      ngay_hl: normalizeDate(initialData.ngay_hl)
    };
  }, [initialData]);

  const handleSubmit = async (data: FormValues) => {
    const payload: GiaMuaInput = {
      ma_vt: vatTu?.uuid || '',
      dvt: donViTinh?.uuid || '',
      ma_kh: nhaCungCap?.uuid || '',
      ma_nt: ngoaiTe?.uuid || '',
      ngay_hl: data.ngay_hl,
      sl_tu: data.sl_tu || 0,
      gia_nt: data.gia_nt,
      status: data.status
    };

    await onSubmit(payload);
  };

  const title = formMode === 'add' ? 'Thêm' : formMode === 'edit' ? 'Sửa' : 'Xem';

  return (
    <DialogV2
      open={open}
      onClose={onClose}
      title={title}
      titleIcon={<AritoIcon icon={281} />}
      disableBackdropClose={false}
      disableEscapeKeyDown={false}
    >
      <AritoFormV2
        mode={formMode}
        hasAritoActionBar={false}
        schema={formSchema}
        onSubmit={handleSubmit}
        initialData={normalizedInitialData}
        className='min-w-[850px]'
        headerFields={<BasicInfo formMode={formMode} searchFields={searchFields} />}
        bottomBar={
          <BottomActionBarV2
            mode={formMode}
            onAdd={onAddButtonClick}
            onEdit={onEditButtonClick}
            onDelete={onDeleteButtonClick}
            onCopy={onCopyButtonClick}
            onClose={onClose}
          />
        }
      />
    </DialogV2>
  );
};

export default FormDialog;
